import React, { useEffect, useState } from "react";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import {
  TextFilter,
  BooleanFilter,
  TableAbstract
} from "shared/components/Components";
import { useMercuryContext } from "../../context";
import { descriptionColumn } from "shared/table/columnsConfig";
import { checkPermissions } from "../../common/utils/checkPermissions";
import PermissionWrapper from "shared/components/PermissionWrapper";

import CustomerApplicationsFilter from "filters/CustomerApplications.filter";
import { Tag } from "antd";
import { Application, ManageRolesProps, Role, Dashboard, UpdatedPermission, Permission } from "./RoleInterfaces";
import RoleRequest from "./RoleRequest";
import TimezoneConverter from "components/timezone-converter";
import TableTitle from "components/table/TableTitle";
import GetActiveStatusIcon from "shared/components/GetActiveStatusIcon";
import "manage/roles/manage-roles.scss";
import OliveDashboardsFilter from "filters/OliveDashboards.filter";
class ManageRoles extends TableAbstract<Role> {
  applicationsList: Application[] = [];
  dashboardsList: Dashboard[] = [];

  constructor(props: ManageRolesProps) {
    super(props);
    this.applicationsList = props.applicationsList || [];
    this.dashboardsList = props.dashboardsList || [];
  }

  defaultSortField = "name";

  FilterComponents = [
    { id: "name", name: "name", title: "Role Name", component: TextFilter, active: true },
    {
      id: "customerApplication",
      name: "customerApplication",
      title: "Customer Application",
      component: (props: any) => (
        <CustomerApplicationsFilter {...props} data={(this.props as ManageRolesProps)?.applicationsList} />
      ),
      active: true,
    },
    {
      id: "dashboards",
      name: "dashboards",
      title: "Dashboards",
      component: (props: any) => (
        <OliveDashboardsFilter {...props} data={(this.props as ManageRolesProps)?.dashboardsList} />
      ),
      active: true,
    },
    { id: "isActive", name: "isActive", title: "Active Status", component: BooleanFilter, active: true },
  ];

  renderTags(text: string | string[]) {
    if (typeof text === "string") {
      return (
        <Tag color="var(--tertiary-color)" style={{ margin: "0 2px 2px 0", padding: "0 4px" }}>
          {text}
        </Tag>
      );
    }
    return text?.map((item, index) => (
      <Tag key={index} color="var(--tertiary-color)" style={{ margin: "0 2px 2px 0", padding: "0 4px" }}>
        {item}
      </Tag>
    ));
  }

  columns = [
    { id: "name", title: "Role Name", dataIndex: "name", sorter: true },
    descriptionColumn,

    {
      id: "customerApplication",
      title: "Customer Application",
      dataIndex: "customerApplicationName",
      render: (record: any) =>
        record ? this.renderTags(record) : "",
    },

    {
      id: "dashboards",
      title: "Dashboards",
      dataIndex: "dashboards",
      render: (value: string[] | string) =>
        value !== "Not set" ? this.renderTags(value) : "",
    },

    {
      id: "isActive",
      title: (<TableTitle title="Active" />) as any,
      dataIndex: "isActive",
      sorter: true,
      width: 80,
      align: "center" as any,
      render: (value: boolean | null | undefined) => <GetActiveStatusIcon status={value} />
    },
    {
      id: "createdAt",
      title: "Created Date",
      dataIndex: "createdAt",
      sorter: true,
      width: 150,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "updatedAt",
      title: "Last Update Date",
      dataIndex: "updatedAt",
      sorter: true,
      width: 150,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
  ];

  renderActions() {
    const userPermissions = this.userContext.userDetails?.permissions || [];
    const hasCreateAccess = checkPermissions(userPermissions, [
      { customerApplication: "Mercury", permissionName: "Manage Roles", action: "create" },
    ]);
    const hasEditAccess = checkPermissions(userPermissions, [
      { customerApplication: "Mercury", permissionName: "Manage Roles", action: "edit" },
    ]);
    const selectedRole = this.state?.selectedRows[0];
    return (
      <>
        <PermissionWrapper
          tooltip={!hasCreateAccess ? "No permission to add a new role." : ""}
          hasPermissions={hasCreateAccess}
          children={
            <RoleRequest
              applicationsList={(this.props as ManageRolesProps).applicationsList}
              dashboardsList={(this.props as ManageRolesProps).dashboardsList}
              permissionsList={(this.props as ManageRolesProps).permissionsList}
              buttonLabel="Add Role"
              disabled={!hasCreateAccess}
              hasPermissions={hasCreateAccess}
              refresh={() => this.reload()}
            />
          }
        />
        <span style={{ display: "inline-block", width: 12 }} />
        <PermissionWrapper
          tooltip={
            !hasEditAccess
              ? "No permission to edit a role."
              : this.state.selectedRows.length !== 1
                ? "Select 1 role to enable edit option"
                : ""
          }
          hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
          children={
            <RoleRequest
              initialValues={{
                ...selectedRole,
                dashboards: Array.isArray(selectedRole?.dashboards)
                  ? selectedRole?.dashboardsId.map(String)
                  : [],
              }}
              applicationsList={(this.props as ManageRolesProps).applicationsList}
              dashboardsList={(this.props as ManageRolesProps).dashboardsList}
              permissionsList={(this.props as ManageRolesProps).permissionsList}
              mode="edit"
              disabled={!hasEditAccess || this.state.selectedRows.length !== 1}
              hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
              refresh={() => this.reload()}
            />
          }
        />
      </>
    );
  }

  rowClassName = (_record: Role): string => "row";

  async fetch(): Promise<any> {
    const { sortOrder, sortField, currentPage, itemsPerPage } = this.state;
    const { dashboardsList } = this.props as ManageRolesProps;
    const requestPayload = {
      filter: this.filtersToQuery(),
      pagination: {
        pageNumber: currentPage || 1,
        resultsPerPage: itemsPerPage,
      },
      sort: {
        [sortField || "createdAt"]: sortOrder === "ascend" ? 1 : -1,
      },
    };

    const response = await this.userContext.umsClient.post(`/ums-v1/roles/search`, requestPayload);
    const documents: Role[] = response?.data?.documents || [];
    const meta = response?.data?.meta ?? {
      total: 0,
      page: currentPage,
      pages: 1,
      skip: 0,
      limit: itemsPerPage,
      match: {},
      sort: { createdAt: -1 },
    };


    const filteredDocuments = documents.map((role) => {
      const dashboardNames = (role.dashboards || [])
        .map((id) => dashboardsList.find((d) => d._id === id)?.name)
        .filter(Boolean);

      return {
        ...role,
        dashboards: dashboardNames.length > 0 ? dashboardNames : "Not set",
        dashboardsId: role.dashboards,
      };
    });

    return {
      data: {
        documents: filteredDocuments,
        meta
      },
    };
  }

  pageTitle = "Manage Roles - Mercury © RWS";
  title = "Manage Roles";
  pageClass = "rolesTable";
}


const ManageRolesTable = () => {
  const context = useMercuryContext();
  const breaks = useBreakpoint();

  const [applicationsList, setApplicationsList] = useState<Application[]>([]);
  const [dashboardsList, setDashboardsList] = useState<Dashboard[]>([]);
  const [permissionsList, setPermissionsList] = useState<UpdatedPermission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: apps }, { data: dashboards }, { data: permissions }] = await Promise.all([
          context.coreClient.get("core-v1/customerApplications/paginate?page=1&limit=500"),
          context.umsClient.get("/ums-v1/dashboards"),
          context.umsClient.get("/ums-v1/permissions"),
        ]);

        setApplicationsList(apps?.[0]?.documents || []);
        setDashboardsList(dashboards || []);
        setPermissionsList(permissions || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [context]);

  if (loading || !applicationsList.length || !dashboardsList.length || !permissionsList.length) return null;

  return (
    <ManageRoles
      {...({
        id: "ManageRoles",
        context,
        breaks,
        useLegacyEndpoints: false,
        hasSidebar: true,
        applicationsList,
        dashboardsList,
        permissionsList: permissionsList as unknown as Permission[]
      } as ManageRolesProps)}
      useEstimatedRowHeight
    />
  );
};

export default ManageRolesTable;
